import { z } from 'zod';
import type { RefinementCtx, ZodEffects, ZodObject, ZodRawShape } from 'zod';

const activeMarkerRequiredMessage = 'Please select an active marker';

const isSameActiveMarkerMessage =
  'The same active marker is selected, please select a different marker.';

const unrefinedChangeActiveMarkerFormSchema = z.object({
  organisationUserId: z.number().optional() // .or(z.undefined()) infers same type as .optional()
});

/**
 * An organisation user ID isn't required when loading the form, so we're using
 * Zod's optional() function to allow the field to be a number or undefined.
 * However, due to an issue where Zod infers the type as
 * "organisationUserId?: number | undefined" but React Hook Form's control and
 * setValue types expect "organisationUser: number | undefined", we have to add
 * TypeScript's "Required" utility type after inferring the type to remove the
 * "?" (optional field) but allow it to be undefined since these are
 * separate but similar concepts. There doesn't appear to be a way to explicitly
 * infer just "organisationUserId: number | undefined" from the Zod schema.
 */
export type ChangeActiveMarkerFormValues = Required<
  z.infer<typeof unrefinedChangeActiveMarkerFormSchema>
>;

const organisationUserIDSchema = unrefinedChangeActiveMarkerFormSchema.superRefine(
  (formData: ChangeActiveMarkerFormValues, ctx: RefinementCtx) => {
    // Ensure that an organisation user ID for the new active marker is specified on
    // save, even if an ID wasn't loaded. This is so that we can display an
    // error message if the user doesn't select an active marker when the form
    // is submitted.
    if (!formData.organisationUserId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: activeMarkerRequiredMessage,
        path: ['organisationUserId']
      });
    }
  }
);

function getSameActiveMarkerSchema(initialOrganisationUser): ZodEffects<ZodObject<ZodRawShape>> {
  return unrefinedChangeActiveMarkerFormSchema.superRefine(
    (formData: ChangeActiveMarkerFormValues, ctx: RefinementCtx) => {
      if (formData.organisationUserId === initialOrganisationUser) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: isSameActiveMarkerMessage,
          path: ['organisationUserId']
        });
      }
    }
  );
}

export function changeActiveMarkerFormSchema({
  organisationUserId
}: z.infer<typeof unrefinedChangeActiveMarkerFormSchema>): ZodEffects<ZodObject<ZodRawShape>> {
  if (organisationUserId) {
    return getSameActiveMarkerSchema(organisationUserId);
  }

  return organisationUserIDSchema;
}
